/** @format */
import axios from 'axios';
import { useContext, useState } from 'react';

import { SubmissionErrorCode } from '@toucan/common/services/application/constants';
import { getConfig } from '@toucan/common/services/nextjs/settings';

import {
    ProductConditionValue,
    ProductSummaryV2,
} from '@toucan/client/global/product';

import { BasketItem, useBasketMaxItemsError } from '@toucan/client/core/basket';

import { BasketContext } from '@toucan/client/shared/context/basket';

/*
 * This is a like-for-like replica of the add to basket handler from the legacy product page
 * Some of the logic could do with a clean-up once the legacy product page is removed
 */
export const useAddToBasketHandler = (product: ProductSummaryV2) => {
    const { basket } = useContext(BasketContext);
    const { addToBasket, isInitialised } = useContext(BasketContext);
    const { showBasketMaxItemsError } = useBasketMaxItemsError();

    const [isLoading, setIsLoading] = useState(false);
    const [isErrorAddingToBasket, setIsErrorAddingToBasket] =
        useState<boolean>(false);

    const isInBasket = basket?.items.some(
        (item: BasketItem) =>
            item.sku === product?.sku || item.id === product?.id
    );

    const isNewProduct = product.condition === ProductConditionValue.NEW;

    const { publicRuntimeConfig } = getConfig();
    const basketMaxItems = publicRuntimeConfig?.basketMaxItems ?? 0;

    const handleAddToBasket = async () => {
        try {
            setIsLoading(true);
            setIsErrorAddingToBasket(false);

            if (
                (isInBasket && !isNewProduct) ||
                (!product.id && !isNewProduct) ||
                !product.modelId
            ) {
                return;
            }

            const image = product?.images?.[0]?.imageUrl;
            await addToBasket({ ...product, image }, 1);
        } catch (error: unknown) {
            if (
                axios.isAxiosError(error) &&
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
                error.response?.data?.errorCode ===
                    SubmissionErrorCode.BASKET_MAX_ITEM_LIMIT_REACHED
            ) {
                showBasketMaxItemsError({
                    isClosable: true,
                    basketMaxItems,
                });
            } else {
                setIsErrorAddingToBasket(true);
            }
        }
    };

    return {
        handleAddToBasket,
        isErrorAddingToBasket,
        isLoading: isLoading || !isInitialised,
        isInBasket,
    };
};
